import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { FiBookmark, FiChevronLeft, FiRefreshCw, FiUpload } from 'react-icons/fi';
import api from '../../api/api';
import { useUserStore } from '../../stores/userStore';
import { AccountBoundary, useRequestScope, useSettings } from '../../features/settings/shared';
import { noticeDate } from '../../features/articles/model';
import { noticeDetailPath, noticeHtml } from '../../features/articles/detailModel';
import PageLayout from '../../components/layout/PageLayout';
import OpenInAppBanner from '../../components/common/OpenInAppBanner';
import SEO from '../../components/common/SEO';
import { Helmet } from 'react-helmet-async';
import baseline from '../../app-ui/baseline';
import { SITE_URL } from '../../utils/env';
import { shareOrCopyUrl } from '../../utils/share';
import useTrackPageView from '../../hooks/useTrackPageView';
import { PAGE_TYPE, CONTENT_TYPE } from '../../constants/gaEnums';
import { trackContentSave, trackShareClick } from '../../lib/gaEvents';
import '../../features/articles/ArticleDetail.css';

export default function NoticeDetail() {
  return <AccountBoundary component={DetailBoundary} />;
}
function DetailBoundary({ user }) {
  const { slug } = useParams(),
    location = useLocation(),
    { locale } = useSettings();
  return <Detail key={`${slug}:${locale}:${location.key}`} {...{ user, slug, locale, location }} />;
}
function Detail({ user, slug, locale, location }) {
  const { style, en, dark } = useSettings(),
    admin = useUserStore((s) => s.isAdmin);
  const navigate = useNavigate(),
    client = useQueryClient(),
    scope = useRequestScope(user);
  const [notice, setNotice] = useState(null),
    [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0),
    [loadError, setLoadError] = useState(false);
  const [busy, setBusy] = useState(false),
    [error, setError] = useState(''),
    [message, setMessage] = useState('');
  const lock = useRef(false),
    shareLock = useRef(false);
  const [sharing, setSharing] = useState(false),
    [expanded, setExpanded] = useState(false);
  const html = useMemo(() => noticeHtml(notice?.content), [notice?.content]);
  useTrackPageView(
    {
      pageType: PAGE_TYPE.NOTICE_POST,
      contentType: CONTENT_TYPE.NOTICE,
      contentId: notice?.id,
      contentName: notice?.title,
    },
    { ready: !!notice?.id },
  );
  useEffect(() => {
    const update = () =>
      setExpanded(parseFloat(getComputedStyle(document.documentElement).fontSize) >= 22.4);
    const observer = new MutationObserver(update);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style', 'class'],
    });
    window.addEventListener('resize', update);
    update();
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);
  useEffect(() => {
    const request = scope();
    setLoading(true);
    setLoadError(false);
    const get = user ? api.getAuth : api.getPublic;
    get(noticeDetailPath(slug), { ...request.options, params: { locale }, skipAuthRecovery: !user })
      .then((response) => {
        const data = response?.data?.data;
        if (!data || data.id == null || (data.isHidden && !admin))
          throw Object.assign(Error('Notice unavailable'), { noticeUnavailable: true });
        if (request.current()) setNotice(data);
      })
      .catch((error) => {
        if (request.current()) {
          if (
            !refresh ||
            error.noticeUnavailable ||
            [401, 403, 404].includes(error.response?.status)
          )
            setNotice(null);
          setLoadError(true);
        }
      })
      .finally(() => {
        if (request.current()) setLoading(false);
        request.finish();
      });
    return request.cancel;
    // The boundary remounts on route, locale and account changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);
  const invalidate = () =>
    client.invalidateQueries({
      predicate: (q) =>
        ['articleDetail', 'notice-list60', 'me', 'profile', 'myProfile'].includes(q.queryKey[0]),
    });
  const mutate = async (kind) => {
    if (!notice || lock.current || loading) return;
    if (!user) {
      navigate(`/login?redirectUrl=${encodeURIComponent(location.pathname + location.search)}`);
      return;
    }
    if (kind !== 'bookmark' && !admin) return;
    if (
      kind === 'hidden' &&
      !window.confirm(
        notice.isHidden
          ? '이 공지를 다시 공개할까요?'
          : '이 공지를 숨길까요? 댓글과 저장 기록은 유지됩니다.',
      )
    )
      return;
    if (kind === 'delete' && !window.confirm('이 공지를 삭제하시겠습니까?')) return;
    const request = scope();
    lock.current = true;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      if (kind === 'bookmark') {
        if (notice.isBookmarked)
          await api.deleteAuth(`/bookmarks/notice/${notice.id}`, request.options);
        else
          await api.postAuth(
            '/bookmarks',
            { itemType: 'NOTICE', itemId: Number(notice.id) },
            request.options,
          );
      } else if (kind === 'pin')
        await api.putAuth(`/notices/${notice.id}`, { isPinned: !notice.isPinned }, request.options);
      else if (kind === 'hidden')
        await api.putAuth(
          `/notices/${notice.id}/visibility`,
          { isHidden: !notice.isHidden },
          request.options,
        );
      else await api.deleteAuth(`/notices/${notice.id}`, request.options);
      if (!request.current()) return;
      if (kind === 'bookmark' && !notice.isBookmarked)
        trackContentSave(
          {
            contentType: CONTENT_TYPE.NOTICE,
            contentId: notice.id,
            contentName: notice.title,
            pageType: PAGE_TYPE.NOTICE_POST,
          },
          user,
        );
      if (kind === 'delete') {
        void invalidate();
        navigate('/notice?category=notice', { replace: true });
        return;
      }
      const field = { bookmark: 'isBookmarked', pin: 'isPinned', hidden: 'isHidden' }[kind];
      setNotice((n) => ({ ...n, [field]: !n[field] }));
      void invalidate();
    } catch {
      if (request.current())
        setError(
          en
            ? 'Could not save this change. Please try again.'
            : '변경하지 못했습니다. 다시 시도해 주세요.',
        );
    } finally {
      if (request.current()) {
        lock.current = false;
        setBusy(false);
      }
      request.finish();
    }
  };
  const share = async () => {
    if (!notice || shareLock.current) return;
    const request = scope();
    shareLock.current = true;
    setSharing(true);
    setError('');
    setMessage('');
    try {
      const result = await shareOrCopyUrl({
        url: `/notice/${notice.id}`,
        title: notice.title,
        text: `${notice.title} — ${en ? 'postpop' : '포스트팝'}`,
      });
      if (!request.current()) return;
      if (result === 'copied')
        setMessage(en ? 'Text and link copied.' : '공유 문구와 링크가 복사되었습니다.');
      if (result !== 'cancelled')
        trackShareClick(
          {
            contentType: CONTENT_TYPE.NOTICE,
            contentId: notice.id,
            contentName: notice.title,
            pageType: PAGE_TYPE.NOTICE_POST,
          },
          user,
        );
    } catch {
      if (request.current())
        setError(
          en ? 'Could not share. Please try again.' : '공유하지 못했습니다. 다시 시도해 주세요.',
        );
    } finally {
      if (request.current()) {
        shareLock.current = false;
        setSharing(false);
      }
      request.finish();
    }
  };
  return (
    <PageLayout paddingTop={{ xs: '16px', lg: '24px' }}>
      <section
        className={`pp-settings pp-article-detail${expanded ? ' pp-article-expanded' : ''}`}
        style={{
          ...style,
          '--article-body': baseline.tokens[dark ? 'darkColors' : 'lightColors'].reviewBody,
          '--article-selected': baseline.tokens[dark ? 'darkColors' : 'lightColors'].accentSelected,
        }}
      >
        <header className="pp-settings-header">
          <button
            aria-label={en ? 'Back' : '뒤로'}
            onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate('/notice'))}
          >
            <FiChevronLeft size={22} />
          </button>
          <h1>{en ? 'Events & Notices' : '이벤트 및 공지사항'}</h1>
          <button
            className="pp-article-refresh"
            aria-label={en ? 'Refresh' : '새로고침'}
            disabled={loading || busy}
            onClick={() => setRefresh((v) => v + 1)}
          >
            <FiRefreshCw size={18} />
          </button>
        </header>
        {loading && (
          <div
            className="pp-article-loading"
            role="status"
            aria-label={en ? 'Loading notice' : '공지 불러오는 중'}
          >
            {Array.from({ length: 5 }, (_, i) => (
              <span key={i} />
            ))}
          </div>
        )}
        {loadError && (
          <div className="pp-article-state" role="alert">
            <p>{en ? 'We could not load this notice.' : '공지 내용을 불러오지 못했습니다.'}</p>
            <button onClick={() => setRefresh((v) => v + 1)}>
              {en ? 'Try again' : '다시 시도'}
            </button>
          </div>
        )}
        {notice && (
          <>
            <OpenInAppBanner type="notice" title={notice.title} image={notice.imageUrl} />
            <SEO
              title={notice.title}
              description={String(notice.content || '')
                .replace(/<[^>]*>/g, '')
                .slice(0, 160)}
              image={notice.imageUrl}
              url={`${SITE_URL}/notice/${notice.slug || notice.id}`}
            />
            <Helmet>
              <script type="application/ld+json">
                {JSON.stringify({
                  '@context': 'https://schema.org',
                  '@type': 'Article',
                  headline: notice.title,
                  image: notice.imageUrl ? [notice.imageUrl] : undefined,
                  datePublished: notice.createdAt?.replace(
                    /(\d+)\.(\d+)\.(\d+)/,
                    (_, y, m, d) => `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`,
                  ),
                  dateModified: notice.updatedAt?.replace(
                    /(\d+)\.(\d+)\.(\d+)/,
                    (_, y, m, d) => `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`,
                  ),
                  author: { '@type': 'Organization', name: 'Postpop', url: SITE_URL },
                }).replace(/</g, '\\u003c')}
              </script>
              <script type="application/ld+json">
                {JSON.stringify({
                  '@context': 'https://schema.org',
                  '@type': 'BreadcrumbList',
                  itemListElement: [
                    { '@type': 'ListItem', position: 1, name: 'Postpop', item: SITE_URL },
                    {
                      '@type': 'ListItem',
                      position: 2,
                      name: 'Notice',
                      item: `${SITE_URL}/notice`,
                    },
                    { '@type': 'ListItem', position: 3, name: notice.title },
                  ],
                }).replace(/</g, '\\u003c')}
              </script>
            </Helmet>
            <article>
              <div className="pp-article-heading">
                <span className="pp-article-category">{en ? 'Notices' : '공지사항'}</span>
                <h2>{notice.title}</h2>
                <time className="pp-article-date">{noticeDate(notice.createdAt, locale)}</time>
                <div className="pp-article-actions">
                  <button
                    aria-pressed={!!notice.isBookmarked}
                    disabled={busy || loading}
                    onClick={() => mutate('bookmark')}
                  >
                    <FiBookmark
                      size={18}
                      fill={notice.isBookmarked ? 'currentColor' : 'none'}
                      strokeWidth={1.8}
                    />
                    {notice.isBookmarked ? (en ? 'Saved' : '저장됨') : en ? 'Save' : '저장'}
                  </button>
                  <button disabled={sharing} onClick={share}>
                    <FiUpload size={18} strokeWidth={1.8} />
                    {en ? 'Share' : '공유'}
                  </button>
                </div>
              </div>
              {error && (
                <p role="alert" className="pp-article-feedback">
                  {error}
                </p>
              )}
              {message && (
                <p role="status" className="pp-article-feedback">
                  {message}
                </p>
              )}
              <div className="pp-article-body" dangerouslySetInnerHTML={{ __html: html }} />
            </article>
            {admin && (
              <aside className="pp-article-admin" aria-label="공지 관리">
                {notice.isHidden && <p role="status">관리자에게만 보이는 숨긴 공지입니다.</p>}
                <Link
                  aria-disabled={busy}
                  onClick={(e) => {
                    if (busy) e.preventDefault();
                  }}
                  to={`/notice/${notice.id}/edit?from=detail`}
                >
                  수정
                </Link>
                <button disabled={busy || loading} onClick={() => mutate('pin')}>
                  {notice.isPinned ? '고정 해제' : '공지 고정'}
                </button>
                <button disabled={busy || loading} onClick={() => mutate('hidden')}>
                  {notice.isHidden ? '다시 공개' : '공지 숨기기'}
                </button>
                <button disabled={busy || loading} onClick={() => mutate('delete')}>
                  삭제
                </button>
              </aside>
            )}
          </>
        )}
      </section>
    </PageLayout>
  );
}
